import { useObjectRef } from '@react-aria/utils';
import type { CSSProperties } from 'react';
import { forwardRef } from 'react';
import type { ButtonProps } from 'react-aria-components';
import { Button, composeRenderProps } from 'react-aria-components';

import { cn, pressScale } from '@infinex/ui-styles';

export type PressableProps = Omit<ButtonProps, 'isPending' | 'slot'>;

/**
 * Pressable is a low-level primitive for creating one-off interactive elements
 * that don't exist in the design system. It should be used sparingly, prefer
 * the appropriate library component alternative where possible.
 */
export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
  function Pressable(props, ref) {
    const domRef = useObjectRef<HTMLButtonElement>(ref);

    return (
      <Button
        {...props}
        ref={domRef}
        className={composeRenderProps(props.className, (className, state) =>
          cn(
            'outline-hidden transition-colors select-none',
            'focus-visible:outline-focusRing',
            'bg-fill text-emphasis',
            { 'bg-fillHover': state.isHovered },
            { 'bg-fillPress fill-default text-default': state.isPressed },
            { 'bg-fillDisabled fill-disabled text-disabled': state.isDisabled },
            className
          )
        )}
        style={pressScale(domRef, props.style as CSSProperties)}
      />
    );
  }
);
